import React, { createContext, useContext, useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { supabase } from '../services/supabase';
import { useAuth } from './AuthContext';
import { requestAndSubscribe, showOSNotification } from '../services/webpush.service';

const NotificationContext = createContext(null);

export const useNotifications = () => useContext(NotificationContext);

export const NotificationProvider = ({ children }) => {
  const { business } = useAuth();
  const [toasts, setToasts]             = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [soundEnabled, setSoundEnabled] = useState(
    () => localStorage.getItem('notif_sound') !== 'off'
  );
  const [notifLog, setNotifLog]   = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const toastIdRef  = useRef(0);
  const knownIdsRef = useRef(new Set());

  const toggleSound = () => {
    setSoundEnabled(prev => {
      const next = !prev;
      localStorage.setItem('notif_sound', next ? 'on' : 'off');
      return next;
    });
  };

  // Supabase-dən bildirişləri yüklə
  const loadNotifLog = useCallback(async () => {
    if (!business?.id) return;
    const { data } = await supabase
      .from('business_notifications')
      .select('*')
      .eq('business_id', business.id)
      .order('created_at', { ascending: false })
      .limit(100);
    if (data) {
      setNotifLog(data);
      setUnreadCount(data.filter(n => !n.read).length);
    }
  }, [business?.id]);

  useEffect(() => { loadNotifLog(); }, [loadNotifLog]);

  const playSound = useCallback(() => {
    if (!soundEnabled) return;
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const play = (freq, start, dur, vol = 0.35) => {
        const o = ctx.createOscillator();
        const g = ctx.createGain();
        o.connect(g); g.connect(ctx.destination);
        o.frequency.value = freq;
        o.type = 'sine';
        g.gain.setValueAtTime(vol, ctx.currentTime + start);
        g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + start + dur);
        o.start(ctx.currentTime + start);
        o.stop(ctx.currentTime + start + dur + 0.01);
      };
      play(880,  0,    0.12);
      play(1100, 0.16, 0.12);
      play(1320, 0.32, 0.18);
    } catch (_) {}
    if (navigator.vibrate) navigator.vibrate([180, 80, 180]);
  }, [soundEnabled]);

  const pushToast = useCallback((type, title, body) => {
    const id = ++toastIdRef.current;
    setToasts(prev => [{ id, type, title, body, ts: Date.now() }, ...prev.slice(0, 4)]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 8000);

    // Supabase-ə yaz
    if (business?.id) {
      supabase.from('business_notifications').insert({
        business_id: business.id,
        type,
        title,
        body,
        read: false,
      }).then(({ data }) => {
        if (data?.[0]) {
          setNotifLog(prev => [data[0], ...prev]);
          setUnreadCount(c => c + 1);
        } else {
          // insert geri qaytarmırsa yenidən yüklə
          loadNotifLog();
          setUnreadCount(c => c + 1);
        }
      });
    }
  }, [business?.id, loadNotifLog]);

  const markAllRead = useCallback(async () => {
    if (!business?.id) return;
    await supabase
      .from('business_notifications')
      .update({ read: true })
      .eq('business_id', business.id)
      .eq('read', false);
    setNotifLog(prev => prev.map(n => ({ ...n, read: true })));
    setUnreadCount(0);
  }, [business?.id]);

  const clearLog = useCallback(async () => {
    if (!business?.id) return;
    await supabase
      .from('business_notifications')
      .delete()
      .eq('business_id', business.id);
    setNotifLog([]);
    setUnreadCount(0);
  }, [business?.id]);

  const dismissToast = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  // Push subscription qeydiyyatı + Supabase-ə saxla
  useEffect(() => {
    if (!business?.id) return;
    (async () => {
      const sub = await requestAndSubscribe();
      if (!sub) return;
      await supabase.from('business_push_subscriptions').upsert({
        business_id: business.id,
        endpoint:    sub.endpoint,
        p256dh:      sub.keys?.p256dh,
        auth:        sub.keys?.auth,
        updated_at:  new Date().toISOString(),
      }, { onConflict: 'endpoint' });
    })();
  }, [business?.id]);

  // Load initial pending count
  useEffect(() => {
    if (!business?.id) return;
    supabase
      .from('flash_bookings')
      .select('id', { count: 'exact', head: true })
      .eq('business_id', business.id)
      .eq('status', 'pending')
      .then(({ count }) => setPendingCount(count || 0));
  }, [business?.id]);

  // Real-time: yeni QR scan (check-in)
  useEffect(() => {
    if (!business?.id) return;
    const channel = supabase
      .channel(`notif-scans-${business.id}`)
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'point_logs',
        filter: `business_id=eq.${business.id}`,
      }, (payload) => {
        const s = payload.new;
        if (knownIdsRef.current.has(`scan-${s.id}`)) return;
        knownIdsRef.current.add(`scan-${s.id}`);
        const name = s.user_name || 'Müştəri';
        const pts = s.points_earned ? ` · +${s.points_earned} xal` : '';
        pushToast('scan', '📲 Yeni Check-in', `${name}${pts}`);
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [business?.id, pushToast]);

  // Real-time: yeni rəy
  useEffect(() => {
    if (!business?.id) return;
    const channel = supabase
      .channel(`notif-reviews-${business.id}`)
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'reviews',
        filter: `business_id=eq.${business.id}`,
      }, (payload) => {
        const r = payload.new;
        if (knownIdsRef.current.has(`review-${r.id}`)) return;
        knownIdsRef.current.add(`review-${r.id}`);
        const stars = '⭐'.repeat(Math.min(r.rating || 0, 5));
        const preview = r.comment ? ` · ${r.comment.slice(0, 50)}` : '';
        pushToast('review', '⭐ Yeni Rəy', `${stars}${preview}`);
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [business?.id, pushToast]);

  // Real-time: yeni pending rezervasiya
  useEffect(() => {
    if (!business?.id) return;
    const channel = supabase
      .channel(`notif-bookings-${business.id}`)
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'flash_bookings',
        filter: `business_id=eq.${business.id}`,
      }, (payload) => {
        const b = payload.new;
        if (b.status !== 'pending') return;
        if (knownIdsRef.current.has(b.id)) return;
        knownIdsRef.current.add(b.id);
        playSound();
        setPendingCount(c => c + 1);
        const name  = b.contact_name || 'Müştəri';
        const time  = b.reservation_time?.slice(0, 5) || '';
        const date  = b.reservation_date || '';
        const title = '🔔 Yeni Rezervasiya';
        const body  = `${name}${date ? ` · ${date}` : ''}${time ? ` saat ${time}` : ''}`;
        pushToast('new', title, body);
        if (document.visibilityState !== 'visible') {
          showOSNotification(title, body, `booking-${b.id}`);
        }
      })
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'flash_bookings',
        filter: `business_id=eq.${business.id}`,
      }, (payload) => {
        const b = payload.new;
        if (b.status !== 'pending') {
          setPendingCount(c => Math.max(0, c - 1));
        }
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [business?.id, playSound, pushToast]);

  // Real-time: QR menüdən ofisiant çağırışı
  useEffect(() => {
    if (!business?.id) return;
    const channel = supabase
      .channel(`notif-waiter-${business.id}`)
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'business_notifications',
        filter: `business_id=eq.${business.id}`,
      }, (payload) => {
        const n = payload.new;
        if (n.type !== 'waiter') return;
        if (knownIdsRef.current.has(`waiter-${n.id}`)) return;
        knownIdsRef.current.add(`waiter-${n.id}`);
        playSound();
        const id = ++toastIdRef.current;
        setToasts(prev => [{ id, type: 'waiter', title: n.title, body: n.body, ts: Date.now() }, ...prev.slice(0, 4)]);
        setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 10000);
        setNotifLog(prev => [n, ...prev.filter(x => x.id !== n.id)]);
        setUnreadCount(c => c + 1);
        if (document.visibilityState !== 'visible') {
          showOSNotification(n.title, n.body, `waiter-${n.id}`);
        }
      })
      .subscribe();
    return () => supabase.removeChannel(channel);
  }, [business?.id, playSound]);

  // Real-time: QR menüdən yeni sifariş
  useEffect(() => {
    if (!business?.id) return;
    const channel = supabase
      .channel(`notif-qrorders-${business.id}`)
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'orders',
        filter: `business_id=eq.${business.id}`,
      }, (payload) => {
        const o = payload.new;
        if (knownIdsRef.current.has(`order-${o.id}`)) return;
        knownIdsRef.current.add(`order-${o.id}`);
        playSound();
        const tableInfo = o.table_number ? `Masa #${o.table_number}` : 'Masa';
        const total = o.total ? ` · ${Number(o.total).toFixed(2)}₼` : '';
        const title = '🛒 Yeni Sifariş';
        const body  = `${tableInfo}${total}`;
        pushToast('order', title, body);
        if (document.visibilityState !== 'visible') {
          showOSNotification(title, body, `order-${o.id}`);
        }
      })
      .subscribe();
    return () => supabase.removeChannel(channel);
  }, [business?.id, playSound, pushToast]);

  // 30 dəq cavabsız xatırlatma
  useEffect(() => {
    if (!business?.id) return;
    const check = async () => {
      const threshold = new Date(Date.now() - 30 * 60 * 1000).toISOString();
      const { data } = await supabase
        .from('flash_bookings')
        .select('id, contact_name, reservation_date, reservation_time')
        .eq('business_id', business.id)
        .eq('status', 'pending')
        .lte('created_at', threshold);
      if (data?.length) {
        playSound();
        pushToast('warn', '⚠️ Cavabsız Rezervasiyalar', `${data.length} rezervasiya 30+ dəqiqədir gözləyir!`);
      }
    };
    const interval = setInterval(check, 60 * 1000);
    return () => clearInterval(interval);
  }, [business?.id, playSound, pushToast]);

  const value = useMemo(() => ({
    pendingCount, setPendingCount,
    toasts, dismissToast,
    soundEnabled, toggleSound,
    playSound, pushToast,
    notifLog, unreadCount, markAllRead, clearLog,
  }), [pendingCount, toasts, dismissToast, soundEnabled, toggleSound, playSound, pushToast, notifLog, unreadCount, markAllRead, clearLog]);

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};
